import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterOutlet } from '@angular/router';
import {
  FuseNavigationService,
  FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseConfig, FuseConfigService } from '@fuse/services/config';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { WebColorScheme } from '@libs/shared/api';
import { SharedModule } from '@libs/shared/shared.module';
import { ColorSchemeMenuButtonComponent } from '@libs/shared/ui/color-scheme-menu-button/color-scheme-menu-button.component';
import { ElasticsearchTermAnalyzerDialogTriggerDirective } from '@libs/shared/ui/elasticsearch-term-analyzer/elasticsearch-term-analyzer-dialog-trigger.directive';
import { MainLoadingBarComponent } from '@libs/shared/ui/main-loading-bar/main-loading-bar.component';
import { ShortcutHelpDialogTriggerDirective } from '@libs/shared/ui/shortcut-help-dialog/shortcut-help-dialog-trigger.directive';
import { AuthService } from '@sprint-backoffice/app/core/auth/auth.service';
import { injectCoreFeature } from '@sprint-backoffice/app/core/state/core.store';
import { UserComponent } from '@sprint-backoffice/app/layout/common/user/user.component';
import { PopulateAllDocumentsLockIndicatorComponent } from '@sprint-backoffice/app/modules/populate-all-documents-lock-indicator/populate-all-documents-lock-indicator.component';
import { Subject, takeUntil } from 'rxjs';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'dense-layout',
  templateUrl: './dense.component.html',
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    MainLoadingBarComponent,
    MatButtonModule,
    MatIconModule,
    ColorSchemeMenuButtonComponent,
    UserComponent,
    RouterOutlet,
    FuseVerticalNavigationComponent,
    ElasticsearchTermAnalyzerDialogTriggerDirective,
    SharedModule,
    ShortcutHelpDialogTriggerDirective,
    PopulateAllDocumentsLockIndicatorComponent,
  ],
})
export class DenseLayoutComponent implements OnInit, OnDestroy {
  config: FuseConfig;
  isScreenSmall: boolean;
  navigationAppearance: 'default' | 'dense' = 'dense';
  core = injectCoreFeature();
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  /**
   * Constructor
   */
  constructor(
    private _fuseMediaWatcherService: FuseMediaWatcherService,
    private _fuseNavigationService: FuseNavigationService,
    private _authService: AuthService,
    private _fuseConfigService: FuseConfigService
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for current year
   */
  get currentYear(): number {
    return new Date().getFullYear();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    this._fuseConfigService.config$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((config: FuseConfig) => {
        // Store the config
        this.config = config;
      });

    // Subscribe to media changes
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the screen is small
        this.isScreenSmall = !matchingAliases.includes('md');

        // Change the navigation appearance
        this.navigationAppearance = this.isScreenSmall ? 'default' : 'dense';
      });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle navigation
   *
   * @param name
   */
  toggleNavigation(name: string): void {
    // Get the navigation
    const navigation =
      this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
        name
      );

    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
    }
  }

  setWebColorScheme(webColorScheme: WebColorScheme): void {
    this.core.updateManagerWebColorScheme(webColorScheme);
  }

  /**
   * Toggle the navigation appearance
   */
  toggleNavigationAppearance(): void {
    this.navigationAppearance =
      this.navigationAppearance === 'default' ? 'dense' : 'default';
  }
}
